import { computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { isNotNil } from '@/utils/object-util'
import type { ActivationLifecycle } from '@/infra/activation'
import type { User } from '@/features/iam/users/User'
import type { JobGist } from '@/features/jobs/JobGist'
import type { Document } from '@/features/docs/Document'
import type { FetchUserFn } from '@/features/iam'
import type { FetchJobGistFn } from '@/features/jobs'
import type {
  PromptDeleteDocumentFn,
  PromptUnlinkDocumentFn,
  ShowRenameDocumentDialogFn,
} from '@/features/docs/index.ts'
import type { DocumentStore } from '@/features/docs/DocumentStore'
import type { DocumentTypeStore } from '@/features/docs/DocumentTypeStore'
import type { DocumentAccess } from '@/features/docs/DocumentAccess'

/**
 * Options for the {@link DocumentViewModel}.
 */
export interface DocumentViewModelOptions {
  /**
   * Whether to fetch the associated jobs on activate.
   */
  fetchJobs: boolean

  /**
   * Whether to fetch the author's user info on activate.
   */
  fetchAuthor: boolean

  /**
   * Will be called when the user interacts with a document item.
   */
  onAction: () => Promise<unknown>

  /**
   * Will be called when removing or deleting a document.
   */
  onRemove: (doc: Document) => Promise<unknown>
}

/**
 * The operation to show in the remove dropdown.
 */
type RemoveOperation = 'Delete' | 'Unlink'

/**
 * View model for a document.
 */
export class DocumentViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The loaded author of the document.
   */
  @observable accessor author: User | null = null

  /**
   * Associated jobs.
   */
  @observable accessor jobs: JobGist[] = []

  constructor(
    public readonly doc: Document,
    private readonly _options: DocumentViewModelOptions,
    private readonly _fetchUser: FetchUserFn,
    private readonly _fetchJobGist: FetchJobGistFn,
    private readonly _showRenameDialog: ShowRenameDocumentDialogFn,
    private readonly _promptDelete: PromptDeleteDocumentFn,
    private readonly _promptUnlink: PromptUnlinkDocumentFn,
    private readonly _documentStore: DocumentStore,
    private readonly _documentTypeStore: DocumentTypeStore,
  ) {
    this.remove = this.remove.bind(this)
    this.unlink = this.unlink.bind(this)
    this.rename = this.rename.bind(this)
    this.delete = this.delete.bind(this)
    this.download = this.download.bind(this)
    this.preview = this.preview.bind(this)
  }

  /**
   * Display name for the type.
   */
  @computed
  get displayTypeName() {
    return this.doc.typeName || 'Unknown type'
  }

  /**
   * The operation to show in the remove dropdown.
   */
  @computed
  get removeOperation(): RemoveOperation {
    if (this.doc.accesses.length <= 1) {
      return 'Delete'
    }

    return 'Unlink'
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetchType()

    if (this._options.fetchAuthor) {
      void this.fetchUser()
    }

    if (this._options.fetchJobs) {
      void this.fetchJobs()
    }
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the document type.
   */
  fetchType = task(() => {
    return this._documentTypeStore.fetchDocumentType(this.doc.typeId)
  })

  /**
   * Fetches the author user's info.
   */
  fetchUser = task(async () => {
    const user = await this._fetchUser(this.doc.authorUserId)
    runInAction(() => (this.author = user))
  })

  /**
   * Fetches the jobs associated with the document.
   */
  fetchJobs = task(async () => {
    const jobs = await Promise.all(
      this.doc.jobIds.map((id) => this._fetchJobGist(id)),
    ).then((r) => r.filter(isNotNil))

    runInAction(() => (this.jobs = jobs))
  })

  /**
   * Shows the rename dialog.
   */
  rename() {
    this._showRenameDialog(this.doc, this._lifecycle.signal)
  }

  /**
   * Shows the delete dialog.
   */
  async delete() {
    const result = await this._promptDelete(this.doc, this._lifecycle.signal)
    if (result) {
      await this._options.onRemove(this.doc)
    }

    return result
  }

  /**
   * If an access is provided, show the unlink dialog.
   * Otherwise, show the delete dialog.
   */
  async remove(access?: DocumentAccess) {
    if (access && access.documentId === this.doc.id) {
      return this.unlink(access)
    } else {
      const deleted = await this.delete()
      return deleted ? ('deleted' as const) : false
    }
  }

  /**
   * Shows the unlink dialog.
   */
  async unlink(access: DocumentAccess) {
    const result = await this._promptUnlink(
      this.doc,
      access,
      this._lifecycle.signal,
    )

    if (result) {
      await this._options.onRemove(this.doc)
    }

    return result
  }

  /**
   * Attempts to download the document.
   */
  download() {
    this._documentStore.download({ document: this.doc, preview: false })
  }

  /**
   * Attempts to preview the document in a new tab.
   */
  preview() {
    this._documentStore.download({ document: this.doc, preview: true })
  }

  /**
   * Triggers the action.
   * What that means depends on the caller.
   */
  action = task.resolved(() => this._options.onAction())
}
